import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Separator } from '../ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from '../ui/sheet';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import {
  ChevronLeft,
  ChevronRight,
  LayoutDashboard,
  TrendingUp,
  Building2,
  CheckSquare,
  Target,
  Users,
  Briefcase,
  BarChart2,
  Link as LinkIcon,
  GitMerge,
  Workflow,
  History,
  Database,
  AlertTriangle,
  Shield,
  Building,
  Settings,
  LogOut,
  Rocket,
  Menu,
  FileText,
  Clock,
  User,
  Boxes,
  Brain,
  Lock,
  Webhook,
  ShieldCheck,
  HelpCircle,
  Activity,
  DollarSign,
  Crosshair,
  RefreshCw,
  ShoppingCart,
  MessageSquare,
  Bell
} from 'lucide-react';
import { useAuth } from '../../lib/auth';
import { useRBAC } from '../../lib/RBACContext';
import { feedbackAPI } from '../../lib/api';

const SECURADO_LOGO_PRIMARY = "https://customer-assets.emergentagent.com/job_streamhub-crm/artifacts/39apig25_Securado%20Logo-01.jpg";
const SECURADO_LOGO_REVERSE = "https://customer-assets.emergentagent.com/job_streamhub-crm/artifacts/690tpc8y_Securado%20Logo-Reverse-01.png";

const iconMap = {
  LayoutDashboard,
  TrendingUp,
  Building2,
  CheckSquare,
  Target,
  Users,
  Briefcase,
  BarChart2,
  Link: LinkIcon,
  GitMerge,
  Workflow,
  History,
  Database,
  AlertTriangle,
  Shield,
  Building,
  Rocket,
  FileText,
  Clock,
  User,
  Settings,
  Boxes,
  Brain,
  Lock,
  Webhook,
  ShieldCheck,
  HelpCircle,
  Activity,
  DollarSign,
  Crosshair,
  RefreshCw,
  ShoppingCart,
  MessageSquare,
  Bell,
};

// Permission requirements for each navigation item
const permissionRequirements = {
  dashboard: 'view_dashboard',
  analytics: 'view_analytics',
  opportunities: 'view_opportunities',
  leads: 'view_opportunities',
  accounts: 'view_accounts',
  activities: 'view_activities',
  'activity-timeline': 'view_activities',
  invoices: 'view_invoices',
  performance: 'view_goals',
  incentives: 'view_goals',
  'org-structure': null, // visible to all
  profile: null, // Always visible
  // DATA SYNC - System Admin only
  integrations: 'system_admin',
  'dashboard-builder': 'manage_dashboard',
  settings: 'system_admin',
  'model-browser': 'system_admin',
  mappings: 'system_admin',
  'data-model': 'system_admin',
  pipelines: 'system_admin',
  runs: 'system_admin',
  datalake: 'system_admin',
  dlq: 'system_admin',
  // Admin section - manage_users or system_admin
  users: 'manage_users',
  roles: 'manage_users',
  'rbac-sync': 'system_admin',
  departments: 'manage_users',
  settings: 'system_admin',
  'system-logs': 'system_admin',
  webhooks: 'system_admin',
  'custom-fields': 'system_admin',
  'data-quality': 'system_admin',
  help: null, // Always visible
  feedback: null, // Always visible
  notifications: null, // Visible to all — the page itself controls what you can see
};

const defaultNavigation = {
  crm: [
    { id: 'dashboard', name: 'Dashboard', href: '/dashboard', icon: 'LayoutDashboard', default: true },
    { id: 'analytics', name: 'AI Analytics', href: '/analytics', icon: 'Brain', default: true },
    { id: 'opportunities', name: 'Opportunities', href: '/opportunities', icon: 'TrendingUp', default: true },
    { id: 'leads', name: 'Leads', href: '/leads', icon: 'Users', default: true },
    { id: 'accounts', name: 'Accounts', href: '/accounts', icon: 'Building2', default: true },
    { id: 'activities', name: 'Activities', href: '/activities', icon: 'CheckSquare', default: true },
    { id: 'activity-timeline', name: 'Timeline', href: '/activity-timeline', icon: 'Clock', default: true },
    { id: 'invoices', name: 'Invoices', href: '/invoices', icon: 'FileText', default: true },
    { id: 'sales-orders', name: 'Sales Orders', href: '/sales-orders', icon: 'ShoppingCart', default: true },
    { id: 'performance', name: 'Performance Hub', href: '/performance', icon: 'Crosshair', default: true },
    { id: 'incentives', name: 'Incentives', href: '/incentives', icon: 'DollarSign', default: true },
    { id: 'org-structure', name: 'Organization', href: '/org-structure', icon: 'Building2', default: true },
    { id: 'profile', name: 'My Profile', href: '/profile', icon: 'User', default: true },
  ],
  etl: [],
  admin: [
    { id: 'users', name: 'Users', href: '/admin/users', icon: 'Users', default: true },
    { id: 'roles', name: 'Roles', href: '/admin/roles', icon: 'Shield', default: true },
    { id: 'integrations', name: 'Integrations', href: '/admin/integrations', icon: 'Database', default: true },
    { id: 'dashboard-builder', name: 'Dashboard Builder', href: '/dashboard-builder', icon: 'BarChart2', default: true },
    { id: 'system-logs', name: 'System Logs', href: '/admin/logs', icon: 'AlertTriangle', default: true },
    { id: 'help', name: 'Help & Support', href: '/help', icon: 'HelpCircle', default: true },
    { id: 'feedback', name: 'Communication Center', href: '/feedback', icon: 'MessageSquare', default: true },
    { id: 'notifications', name: 'Notification Config', href: '/admin/notifications', icon: 'Bell', default: true },
  ],
};

const getStoredNavigation = () => {
  try {
    const stored = localStorage.getItem('securado_nav_preferences');
    if (stored) {
      const parsed = JSON.parse(stored);
      // Validate stored preferences - ensure all items have default values if not set
      const defaults = {};
      Object.values(defaultNavigation).flat().forEach(item => {
        // If item is not in stored preferences, use default
        // If item is explicitly set, use that value
        defaults[item.id] = parsed[item.id] !== undefined ? parsed[item.id] : item.default;
      });
      return defaults;
    }
  } catch (e) {
    console.error('Error reading navigation preferences:', e);
  }
  // Return default enabled items
  const defaults = {};
  Object.values(defaultNavigation).flat().forEach(item => {
    defaults[item.id] = item.default;
  });
  return defaults;
};

const saveNavigationPreferences = (prefs) => {
  try {
    localStorage.setItem('securado_nav_preferences', JSON.stringify(prefs));
  } catch (e) {
    console.error('Error saving navigation preferences:', e);
  }
};

export function Sidebar({ mobile = false }) {
  const [collapsed, setCollapsed] = useState(false);
  const [navPreferences, setNavPreferences] = useState(getStoredNavigation);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [feedbackUnreadCount, setFeedbackUnreadCount] = useState(0);
  const location = useLocation();
  const { user, logout } = useAuth();
  const { hasPermission, loading: rbacLoading, roles, permissions } = useRBAC();

  useEffect(() => {
    saveNavigationPreferences(navPreferences);
  }, [navPreferences]);

  useEffect(() => {
    let mounted = true;
    const storageKey = 'feedback_notification_seen';
    const loadUnread = async () => {
      try {
        const res = await feedbackAPI.getNotifications({ limit: 100 });
        const notifications = res.data?.notifications || [];
        const seenMap = JSON.parse(localStorage.getItem(storageKey) || '{}');
        const unread = notifications.filter((item) => {
          const lastSeen = seenMap[item.id];
          const lastActivity = item.created_at;
          return !lastSeen || new Date(lastActivity).getTime() > new Date(lastSeen).getTime();
        }).length;
        if (mounted) setFeedbackUnreadCount(unread);
      } catch {
        if (mounted) setFeedbackUnreadCount(0);
      }
    };
    loadUnread();
    window.addEventListener('focus', loadUnread);
    return () => {
      mounted = false;
      window.removeEventListener('focus', loadUnread);
    };
  }, []);

  const toggleNavItem = (itemId) => {
    setNavPreferences(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Filter items based on both preferences AND permissions
  // Memoized to recalculate when rbacLoading or permissions change
  const getVisibleItems = React.useCallback((items) => {
    // While RBAC is still loading, show all default items
    if (rbacLoading) {
      return items.filter(item => {
        if (navPreferences[item.id] === false) return false;
        return item.default !== false;
      });
    }
    
    return items.filter(item => {
      // Check if user disabled it in preferences
      if (navPreferences[item.id] === false) return false;
      
      // Check permission requirement
      const requiredPermission = permissionRequirements[item.id];
      
      // Always show items with no permission requirement (like profile)
      if (requiredPermission === null) return true;
      
      // If no specific permission defined, show by default
      if (!requiredPermission) return true;
      
      // Check if user has the required permission using permissions array directly
      if (permissions.includes('admin:*')) return true;
      return permissions.includes(requiredPermission);
    });
  }, [navPreferences, rbacLoading, permissions]);

  // Check if user has access to a section
  const hasSectionAccess = (section) => {
    if (section === 'crm') return true;
    if (section === 'etl') return false;
    if (section === 'admin') {
      // Only actual admin roles can see admin section — not just permission checks
      const userRoles = JSON.parse(localStorage.getItem('user') || '{}').roles || [];
      return userRoles.some(r => ['admin', 'system_admin', 'sales_admin'].includes(r));
    }
    return true;
  };

  // List of pages that should preserve filter params
  const FILTER_ENABLED_PATHS = [
    '/dashboard',
    '/opportunities',
    '/leads',
    '/accounts',
    '/activities',
    '/analytics',
    '/invoices',
    '/activity-timeline',
    '/performance',
    '/incentives'
  ];

  const NavItem = ({ item }) => {
    const Icon = iconMap[item.icon] || LayoutDashboard;
    const isActive = location.pathname === item.href || location.pathname.startsWith(item.href + '/');
    
    const shouldPreserveParams = FILTER_ENABLED_PATHS.includes(location.pathname) && FILTER_ENABLED_PATHS.includes(item.href);
    const linkTo = shouldPreserveParams ? `${item.href}${location.search}` : item.href;

    const content = (
      <Link
        to={linkTo}
        className={cn(
          'flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-all duration-200 group',
          isActive
            ? 'bg-[#800000]/8 text-[#800000] font-semibold'
            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100 font-medium'
        )}
        data-testid={`nav-${item.name.toLowerCase().replace(/\s/g, '-')}`}
      >
        <Icon className={cn('h-4 w-4 shrink-0 transition-colors', isActive ? 'text-[#800000]' : 'text-slate-400 group-hover:text-slate-600')} />
        {(!collapsed || mobile) && (
          <span className="truncate leading-none">{item.name}</span>
        )}
        {item.id === 'feedback' && feedbackUnreadCount > 0 && (!collapsed || mobile) && (
          <Badge className="ml-auto text-[10px] h-5 bg-[#800000] text-white px-1.5">{Math.min(feedbackUnreadCount, 99)}</Badge>
        )}
        {/* Active indicator dot */}
        {isActive && (!collapsed || mobile) && item.id !== 'feedback' && (
          <span className="ml-auto w-1.5 h-1.5 rounded-full bg-[#800000] shrink-0" />
        )}
      </Link>
    );

    if (collapsed && !mobile) {
      return (
        <TooltipProvider key={item.href}>
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>{content}</TooltipTrigger>
            <TooltipContent side="right" className="bg-slate-900 text-white border-slate-700 text-xs">
              {item.name}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return content;
  };

  const NavigationSettings = () => {
    // Helper to check if item is accessible
    const canAccessItem = (item) => {
      const requiredPermission = permissionRequirements[item.id];
      if (requiredPermission === null) return true;
      if (!requiredPermission) return true;
      return hasPermission(requiredPermission);
    };
    
    return (
    <Sheet open={settingsOpen} onOpenChange={setSettingsOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="text-gray-400 hover:text-white hover:bg-white/10"
          data-testid="nav-settings-button"
        >
          <Settings className="h-4 w-4" />
        </Button>
      </SheetTrigger>
      <SheetContent className="bg-white text-slate-900 border-r border-slate-200 overflow-hidden" side="left">
        <SheetHeader>
          <SheetTitle className="text-white">Navigation Settings</SheetTitle>
          <SheetDescription className="text-gray-400">
            Select which menu items to show in the sidebar.
            {roles.length > 0 && (
              <span className="block mt-1 text-xs">
                Your roles: {roles.join(', ')}
              </span>
            )}
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-120px)] mt-6 pr-4">
          <div className="space-y-6">
            {/* CRM Section */}
            <div>
              <h4 className="text-sm font-semibold text-[#800000] uppercase tracking-wider mb-3">
                CRM Platform
              </h4>
              <div className="space-y-3">
                {defaultNavigation.crm.map((item) => {
                  const accessible = canAccessItem(item);
                  return (
                  <div key={item.id} className="flex items-center space-x-3">
                    <Checkbox
                      id={`nav-${item.id}`}
                      checked={navPreferences[item.id] !== false && accessible}
                      onCheckedChange={() => accessible && toggleNavItem(item.id)}
                      disabled={!accessible}
                      className="border-gray-600 data-[state=checked]:bg-[#800000] data-[state=checked]:border-[#800000] disabled:opacity-50"
                    />
                    <Label 
                      htmlFor={`nav-${item.id}`} 
                      className={cn(
                        "text-sm cursor-pointer flex items-center gap-2",
                        accessible ? "text-gray-300" : "text-gray-500"
                      )}
                    >
                      {React.createElement(iconMap[item.icon], { className: "h-4 w-4" })}
                      {item.name}
                      {!accessible && <Lock className="h-3 w-3 text-gray-500" />}
                    </Label>
                  </div>
                  );
                })}
              </div>
            </div>

            <Separator className="bg-gray-700" />

            {/* ETL Section */}
            {hasSectionAccess('etl') && (
            <div>
              <h4 className="text-sm font-semibold text-[#800000] uppercase tracking-wider mb-3">
                DATA SYNC
              </h4>
              <div className="space-y-3">
                {defaultNavigation.etl.map((item) => {
                  const accessible = canAccessItem(item);
                  return (
                  <div key={item.id} className="flex items-center space-x-3">
                    <Checkbox
                      id={`nav-${item.id}`}
                      checked={navPreferences[item.id] !== false && accessible}
                      onCheckedChange={() => accessible && toggleNavItem(item.id)}
                      disabled={!accessible}
                      className="border-gray-600 data-[state=checked]:bg-[#800000] data-[state=checked]:border-[#800000] disabled:opacity-50"
                    />
                    <Label 
                      htmlFor={`nav-${item.id}`} 
                      className={cn(
                        "text-sm cursor-pointer flex items-center gap-2",
                        accessible ? "text-gray-300" : "text-gray-500"
                      )}
                    >
                      {React.createElement(iconMap[item.icon], { className: "h-4 w-4" })}
                      {item.name}
                      {!accessible && <Lock className="h-3 w-3 text-gray-500" />}
                    </Label>
                  </div>
                  );
                })}
              </div>
            </div>
            )}

            {hasSectionAccess('etl') && <Separator className="bg-gray-700" />}

            {/* Admin Section */}
            {hasSectionAccess('admin') && (
            <div>
              <h4 className="text-sm font-semibold text-[#800000] uppercase tracking-wider mb-3">
                Admin
              </h4>
              <div className="space-y-3">
                {defaultNavigation.admin.map((item) => {
                  const accessible = canAccessItem(item);
                  return (
                  <div key={item.id} className="flex items-center space-x-3">
                    <Checkbox
                      id={`nav-${item.id}`}
                      checked={navPreferences[item.id] !== false && accessible}
                      onCheckedChange={() => accessible && toggleNavItem(item.id)}
                      disabled={!accessible}
                      className="border-gray-600 data-[state=checked]:bg-[#800000] data-[state=checked]:border-[#800000] disabled:opacity-50"
                    />
                    <Label 
                      htmlFor={`nav-${item.id}`} 
                      className={cn(
                        "text-sm cursor-pointer flex items-center gap-2",
                        accessible ? "text-gray-300" : "text-gray-500"
                      )}
                    >
                      {React.createElement(iconMap[item.icon], { className: "h-4 w-4" })}
                      {item.name}
                      {!accessible && <Lock className="h-3 w-3 text-gray-500" />}
                    </Label>
                  </div>
                  );
                })}
              </div>
            </div>
            )}
            
            <div className="pt-4">
              <Button 
                variant="outline" 
                className="w-full border-[#800000] text-[#800000] hover:bg-[#800000] hover:text-white"
                onClick={() => {
                  const defaults = {};
                  Object.values(defaultNavigation).flat().forEach(item => {
                    defaults[item.id] = item.default;
                  });
                  setNavPreferences(defaults);
                }}
              >
                Reset to Defaults
              </Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
    );
  };

  return (
    <aside
      className={cn(
        mobile
          ? 'h-full w-full bg-white'
          : 'hidden lg:block fixed left-0 top-0 z-40 h-screen transition-[width] duration-300 ease-in-out bg-white border-r border-slate-200/80',
        !mobile && (collapsed ? 'w-20' : 'w-64')
      )}
      style={mobile ? undefined : { boxShadow: '4px 0 24px rgba(0,0,0,0.04)' }}
      data-testid="sidebar"
    >
      <div className="flex h-full flex-col overflow-hidden">
        {/* Header — clean white with logo */}
        <div className="flex h-24 items-center justify-between px-4 border-b border-slate-100">
          {!collapsed && (
            <div className="flex items-center gap-2.5">
              <img 
                src={SECURADO_LOGO_PRIMARY} 
                alt="Securado" 
                className="w-auto"
                style={{ height: '72px', maxHeight: '72px' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>
          )}
          {collapsed && (
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#800000] to-[#c0392b] flex items-center justify-center shadow-md">
              <span className="text-white font-bold text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>S</span>
            </div>
          )}
          {!mobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCollapsed(!collapsed)}
              className="text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg h-8 w-8"
              data-testid="sidebar-toggle"
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </Button>
          )}
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 px-3 py-4 overflow-y-auto">
          {/* CRM Platform */}
          {getVisibleItems(defaultNavigation.crm).length > 0 && (
            <div className="mb-5">
              {!collapsed && (
                <p className="px-3 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em]">
                  CRM PLATFORM
                </p>
              )}
              <nav className="space-y-0.5">
                {getVisibleItems(defaultNavigation.crm).map((item) => (
                  <NavItem key={item.href} item={item} />
                ))}
              </nav>
            </div>
          )}

          {getVisibleItems(defaultNavigation.crm).length > 0 && getVisibleItems(defaultNavigation.etl).length > 0 && (
            <div className="my-3 mx-3 h-px bg-slate-100" />
          )}

          {/* DATA SYNC */}
          {getVisibleItems(defaultNavigation.etl).length > 0 && (
            <div className="mb-5">
              {!collapsed && (
                <p className="px-3 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em]">
                  DATA SYNC
                </p>
              )}
              <nav className="space-y-0.5">
                {getVisibleItems(defaultNavigation.etl).map((item) => (
                  <NavItem key={item.href} item={item} />
                ))}
              </nav>
            </div>
          )}

          {(getVisibleItems(defaultNavigation.crm).length > 0 || getVisibleItems(defaultNavigation.etl).length > 0) && 
           getVisibleItems(defaultNavigation.admin).length > 0 && (
            <div className="my-3 mx-3 h-px bg-slate-100" />
          )}

          {/* Admin */}
          {getVisibleItems(defaultNavigation.admin).length > 0 && (
            <div>
              {!collapsed && (
                <p className="px-3 mb-2 text-[10px] font-bold text-slate-400 uppercase tracking-[0.1em]">
                  ADMIN
                </p>
              )}
              <nav className="space-y-0.5">
                {getVisibleItems(defaultNavigation.admin).map((item) => (
                  <NavItem key={item.href} item={item} />
                ))}
              </nav>
            </div>
          )}
        </ScrollArea>

        {/* User section — clean bottom bar */}
        <div className="border-t border-slate-100 p-3">
          {(!collapsed || mobile) && permissions.includes('system_admin') && (
            <Link to="/admin/settings" className="mb-3 flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50" data-testid="bottom-settings-link">
              <Settings className="h-4 w-4 text-[#800000]" /> Settings
            </Link>
          )}
          <div className={cn('flex items-center', collapsed && !mobile ? 'justify-center' : 'gap-2.5')}>
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#800000] to-[#c0392b] flex items-center justify-center text-xs font-bold text-white shadow-sm shrink-0 overflow-hidden">
              {user?.avatar_url ? <img src={user.avatar_url} alt={user?.name || 'User'} className="w-full h-full object-cover" /> : (user?.name?.charAt(0) || 'U')}
            </div>
            {(!collapsed || mobile) && (
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-slate-800 truncate">{user?.name || 'User'}</p>
                <p className="text-[10px] text-slate-400 truncate">{user?.email}</p>
              </div>
            )}
            {(!collapsed || mobile) && (
              <div className="flex items-center gap-0.5">
                <NavigationSettings />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={logout}
                  className="text-slate-400 hover:text-[#800000] hover:bg-red-50 h-7 w-7 rounded-lg"
                  data-testid="logout-button"
                >
                  <LogOut className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </aside>
  );
}
